import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Wrench, Settings, Zap, Package, ChevronRight, ShieldCheck, Clock, Award, Star } from 'lucide-react';

const services = [
  { icon: <Wrench size={32} />, title: 'V-Twin Repair', desc: 'Comprehensive diagnostics and repair for all American V-Twin platforms. Done right the first time.' },
  { icon: <Settings size={32} />, title: 'Custom Builds', desc: 'Your vision, our craft. We take custom builds from concept to completion with precision and real mechanical expertise.' },
  { icon: <Zap size={32} />, title: 'Performance Work', desc: 'Cam swaps, head work, big bore kits, and tuning to extract every bit of power from your engine.' },
  { icon: <Package size={32} />, title: 'Rare Parts Sourcing', desc: 'We hunt down hard-to-find and discontinued parts so your build never hits a dead end.' },
];

const whyUs = [
  { icon: <ShieldCheck size={22} />, title: 'Real Experience', desc: "Decades on American V-Twins means we've seen it all and fixed it all." },
  { icon: <Award size={22} />, title: 'Quality First', desc: "We don't cut corners. Every job gets the attention and parts it deserves." },
  { icon: <Clock size={22} />, title: 'Honest Communication', desc: "You'll know exactly what we're doing and why before we turn a wrench." },
  { icon: <Star size={22} />, title: '5-Star Rated', desc: 'One review, five stars, because we do it right the first time.' },
];

const css = `
  .hero {
    min-height: 100vh;
    background: #0d0d0d;
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
    padding-top: 72px;
  }
  .hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 80% 60% at 70% 50%, rgba(192,57,43,0.13) 0%, transparent 70%),
      radial-gradient(ellipse 40% 40% at 25% 85%, rgba(212,160,23,0.06) 0%, transparent 60%);
  }
  .hero-grid {
    max-width: 1180px; margin: 0 auto; padding: 80px 24px;
    display: grid; grid-template-columns: 1fr 1fr;
    align-items: center; gap: 60px; position: relative;
  }
  .hero-eyebrow {
    display: inline-flex; align-items: center; gap: 10px;
    font-family: 'Oswald', sans-serif; font-size: 0.72rem; font-weight: 600;
    letter-spacing: 0.28em; text-transform: uppercase; color: #d4a017; margin-bottom: 22px;
  }
  .hero-eyebrow::before {
    content: ''; display: block; width: 32px; height: 2px; background: #d4a017;
  }
  .hero h1 {
    font-family: 'Oswald', sans-serif;
    font-size: clamp(3.2rem, 7vw, 5.8rem);
    font-weight: 700; text-transform: uppercase; color: #fff;
    line-height: 0.95; margin-bottom: 28px;
  }
  .hero h1 .accent { color: #c0392b; display: block; }
  .hero-desc { font-size: 1.08rem; color: #888; line-height: 1.8; max-width: 460px; margin-bottom: 40px; }
  .hero-actions { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 48px; }
  .hero-stats {
    display: flex; gap: 36px; padding-top: 36px;
    border-top: 1px solid rgba(255,255,255,0.07);
  }
  .stat-num {
    font-family: 'Bebas Neue', 'Oswald', sans-serif; font-size: 2.4rem;
    color: #c0392b; line-height: 1; margin-bottom: 4px;
  }
  .stat-label { font-size: 0.72rem; letter-spacing: 0.14em; text-transform: uppercase; color: #555; }
  .hero-visual {
    display: flex; align-items: center; justify-content: center;
  }
  .logo-wrap {
    position: relative; width: 340px; height: 340px;
    display: flex; align-items: center; justify-content: center;
  }
  .logo-wrap img {
    width: 260px; height: 260px; object-fit: contain; position: relative; z-index: 2;
    filter: drop-shadow(0 0 48px rgba(192,57,43,0.35));
  }
  .ring {
    position: absolute; border-radius: 50%;
    border: 1px solid rgba(192,57,43,0.22);
    animation: spin 22s linear infinite;
  }
  .ring-1 { inset: 0; }
  .ring-2 { inset: 24px; border-style: dashed; border-color: rgba(212,160,23,0.14); animation-duration: 16s; animation-direction: reverse; }
  .ring-glow {
    position: absolute; inset: -10px; border-radius: 50%;
    background: radial-gradient(circle, rgba(192,57,43,0.14) 0%, transparent 70%);
    animation: pulse 3.5s ease-in-out infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.1);opacity:.6} }

  .services-sec { background: #111; padding: 96px 0; }
  .services-header { max-width: 1180px; margin: 0 auto 52px; padding: 0 24px; }
  .services-grid {
    max-width: 1180px; margin: 0 auto; padding: 0 24px;
    display: grid; grid-template-columns: repeat(4,1fr); gap: 20px;
  }
  .svc-card {
    background: #1a1a1a; border: 1px solid rgba(255,255,255,0.06);
    padding: 34px 26px; position: relative; overflow: hidden;
    transition: border-color 0.3s, transform 0.3s;
  }
  .svc-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: #c0392b; transform: scaleX(0); transition: transform 0.3s;
  }
  .svc-card:hover { border-color: rgba(192,57,43,0.3); transform: translateY(-4px); }
  .svc-card:hover::before { transform: scaleX(1); }
  .svc-icon { color: #c0392b; margin-bottom: 18px; }
  .svc-card h3 { font-size: 1.1rem; font-weight: 600; color: #fff; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 10px; }
  .svc-card p { font-size: 0.86rem; color: #888; line-height: 1.7; }
  .services-cta { text-align: center; margin-top: 44px; }

  .why-sec { background: #0d0d0d; padding: 96px 0; }
  .why-inner {
    max-width: 1180px; margin: 0 auto; padding: 0 24px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start;
  }
  .why-left .btn-primary { margin-top: 36px; }
  .why-list { display: flex; flex-direction: column; gap: 26px; }
  .why-item { display: flex; gap: 18px; align-items: flex-start; }
  .why-icon {
    flex-shrink: 0; width: 46px; height: 46px;
    background: rgba(192,57,43,0.1); border: 1px solid rgba(192,57,43,0.22);
    display: flex; align-items: center; justify-content: center; color: #c0392b;
  }
  .why-item h4 { font-size: 0.98rem; font-weight: 600; color: #fff; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 6px; }
  .why-item p { font-size: 0.86rem; color: #888; line-height: 1.65; }

  .cta-band { background: #c0392b; padding: 72px 0; position: relative; overflow: hidden; }
  .cta-band::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(0,0,0,0.18) 0%, transparent 50%); }
  .cta-inner {
    max-width: 1180px; margin: 0 auto; padding: 0 24px; position: relative;
    display: flex; justify-content: space-between; align-items: center; gap: 40px; flex-wrap: wrap;
  }
  .cta-inner h2 { font-size: clamp(1.6rem,3.5vw,2.6rem); text-transform: uppercase; color: #fff; margin-bottom: 8px; }
  .cta-inner p { color: rgba(255,255,255,0.75); font-size: 1rem; }
  .cta-actions { display: flex; gap: 16px; flex-wrap: wrap; flex-shrink: 0; }
  .btn-white {
    display: inline-flex; align-items: center; gap: 10px;
    background: #fff; color: #c0392b; padding: 14px 28px;
    font-family: 'Oswald', sans-serif; font-size: 0.9rem; font-weight: 700;
    letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; transition: all 0.2s;
  }
  .btn-white:hover { background: #f5f0e8; transform: translateY(-2px); }
  .btn-ghost {
    display: inline-flex; align-items: center; gap: 10px;
    background: transparent; color: #fff; border: 2px solid rgba(255,255,255,0.55);
    padding: 12px 26px; font-family: 'Oswald', sans-serif; font-size: 0.9rem; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase; text-decoration: none; transition: all 0.2s;
  }
  .btn-ghost:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

  @media (max-width:1024px) {
    .services-grid { grid-template-columns: repeat(2,1fr); }
    .logo-wrap { width: 260px; height: 260px; }
    .logo-wrap img { width: 200px; height: 200px; }
  }
  @media (max-width:768px) {
    .hero-grid { grid-template-columns: 1fr; }
    .hero-visual { display: none; }
    .hero-stats { gap: 20px; }
    .why-inner { grid-template-columns: 1fr; gap: 44px; }
  }
`;

export default function Home() {
  return (
    <>
      <style>{css}</style>

      {/* HERO */}
      <section className="hero">
        <div className="hero-grid">
          <div>
            <span className="hero-eyebrow">New Holland, PA</span>
            <h1>
              American<br />
              <span className="accent">V-Twin</span><br />
              Specialists
            </h1>
            <p className="hero-desc">
              Wengers Cycle Works specializes in V-Twin repair, custom builds, and performance work.
              We also source rare and hard-to-find parts to keep your build moving forward.
            </p>
            <div className="hero-actions">
              <a href="tel:7172785897" className="btn-primary">
                <Phone size={16} /> Call Us Today
              </a>
              <Link to="/services" className="btn-outline">
                Our Services <ChevronRight size={16} />
              </Link>
            </div>
            <div className="hero-stats">
              <div>
                <div className="stat-num">V-Twin</div>
                <div className="stat-label">Specialists</div>
              </div>
              <div>
                <div className="stat-num">5★</div>
                <div className="stat-label">Rated</div>
              </div>
              <div>
                <div className="stat-num">PA</div>
                <div className="stat-label">Lancaster County</div>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <div className="logo-wrap">
              <div className="ring-glow" />
              <div className="ring ring-1" />
              <div className="ring ring-2" />
              <img src="/logo.png" alt="Wengers Cycle Works" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="services-sec">
        <div className="services-header">
          <span className="section-label">What We Do</span>
          <h2 className="section-title">Our Services</h2>
          <div className="divider" />
          <p className="section-subtitle">
            From routine maintenance to full custom builds, we handle it all with precision and real experience.
          </p>
        </div>
        <div className="services-grid">
          {services.map((s, i) => (
            <div className="svc-card" key={i}>
              <div className="svc-icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="services-cta">
          <Link to="/services" className="btn-primary">
            View All Services <ChevronRight size={16} />
          </Link>
        </div>
      </section>

      {/* WHY US */}
      <section className="why-sec">
        <div className="why-inner">
          <div className="why-left">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title">Built on Quality &amp; Real Experience</h2>
            <div className="divider" />
            <p className="section-subtitle">
              We're not a dealership. We're riders and builders who care deeply about doing the work right.
              Every bike that rolls into our shop gets treated like it's our own.
            </p>
            <Link to="/about" className="btn-primary">
              Learn About Us <ChevronRight size={16} />
            </Link>
          </div>
          <div className="why-list">
            {whyUs.map((item, i) => (
              <div className="why-item" key={i}>
                <div className="why-icon">{item.icon}</div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="cta-band">
        <div className="cta-inner">
          <div>
            <h2>Ready to Get Your Bike Right?</h2>
            <p>Call us today or stop by in New Holland. We'll give you straight answers.</p>
          </div>
          <div className="cta-actions">
            <a href="tel:7172785897" className="btn-white">
              <Phone size={16} /> (717) 278-5897
            </a>
            <Link to="/contact" className="btn-ghost">Get in Touch</Link>
          </div>
        </div>
      </section>
    </>
  );
}
